import { useEffect, useState, useSyncExternalStore } from 'react'
import { displayTitle } from '../../lib/displayName'
import { commandPayload, commands, isLiveRuntime } from '../../lib/runtime'
import { getActivePrototypeRuntime, subscribePrototypeRuntime } from '../../stores/activeRuntime'
import { enumLabel, enumShort } from './chefShared'

/**
 * Unpaid meal calendar reservations (holds on days awaiting cart payment).
 * These belong on the Meal Calendar and Cart until checked out/paid, not in
 * the customer's placed orders or message threads.
 */
export const isUnpaidMealPlanHold = (order: Record<string, unknown>): boolean => {
  const status = enumShort(order.status, '')
  if (status !== 'pending_payment' && status !== 'draft') return false
  if (Number(order.amount_paid_minor ?? 0) > 0) return false

  let meta: Record<string, unknown> = {}
  if (typeof order.metadata === 'string') {
    try {
      meta = JSON.parse(order.metadata)
    } catch {
      meta = {}
    }
  } else if (order.metadata && typeof order.metadata === 'object') {
    meta = order.metadata as Record<string, unknown>
  }

  if (meta.reservation === true || meta.reservation === 'true') return true
  if (meta.source === 'marketplace:create_reservation:v1') return true

  const id = String(order.id ?? '')
  if (id.startsWith('hold-')) return true

  const ref = String(order.reference ?? '')
  if (ref.startsWith('RES-')) return true

  // In live runtime, projection records omit the unmapped metadata column.
  // Any unpaid order carrying a scheduled_at timestamp belongs to the Meal
  // Calendar until paid via the cart.
  if (order.scheduled_at) {
    const raw = String(order.scheduled_at).trim()
    if (raw) {
      const scheduledTime = new Date(raw).getTime()
      if (!Number.isNaN(scheduledTime) && scheduledTime > 0) {
        return true
      }
    }
  }

  return false
}

/**
 * Shared presentation helpers for an order row.
 *
 * The `marketplace.marketplace_orders` projection is deliberately lean (see
 * `docs/` on order detail vs projection): it carries ids, money and status but
 * no reference string, no summary and no line items. Every surface that lists
 * orders therefore has to *derive* its labels, and each one used to derive them
 * differently — the Inbox picker fell back to the literal strings "Order" and
 * "Order conversation" for every row, which is unreadable when you have three.
 * These helpers are the single answer to "what do we call this order".
 */

export type OrderRecord = Record<string, unknown>

import { orderReference } from './orderReference'
export { orderReference }

/** "24 Aug, 7:02 PM" — empty string when the timestamp is missing or unparseable. */
export const formatOrderWhen = (ts: unknown): string => {
  const raw = String(ts ?? '')
  if (!raw) return ''
  const date = new Date(raw)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

const naira = (minor: unknown): string => `₦${(Number(minor ?? 0) / 100).toLocaleString()}`

/**
 * The title an order goes by in a picker or list: when it was placed and what
 * it cost — the two facts that actually tell three orders apart. Falls back to
 * the derived reference when the row has no usable timestamp.
 */
export const orderTitle = (order: OrderRecord): string => {
  const when = formatOrderWhen(order.created_at ?? order.scheduled_at)
  const total = naira(order.total_minor)
  return when ? `${when} · ${total}` : `${orderReference(order)} · ${total}`
}

/**
 * The supporting line: the dishes when we know them, else whatever the lean
 * row can say for itself (item count, delivery choice, status).
 */
export const orderSummary = (order: OrderRecord, items?: readonly string[]): string => {
  if (items && items.length > 0) {
    const head = items.slice(0, 2).join(', ')
    return items.length > 2 ? `${head} +${items.length - 2} more` : head
  }
  const explicit = String(order.summary ?? '').trim()
  if (explicit) return explicit
  const count = Number(order.item_count ?? order.items_count ?? 0)
  const parts = [
    count > 0 ? `${count} item${count === 1 ? '' : 's'}` : '',
    order.delivery_choice ? (String(order.delivery_choice).toLowerCase().endsWith('chowdash') ? 'Delivery' : enumLabel(order.delivery_choice)) : '',
    order.status ? enumLabel(order.status) : '',
  ].filter(Boolean)
  return parts.length > 0 ? parts.join(' · ') : 'Order details'
}

/** Cap on detail fetches per render pass — a picker never shows more than this. */
const ITEM_FETCH_LIMIT = 96

/** Successful names remain cached across mounts within one authenticated runtime. */
const sessionCaches = new WeakMap<object, Map<string, readonly string[]>>()
const namesCache = (session: object) => {
  let cache = sessionCaches.get(session)
  if (!cache) { cache = new Map(); sessionCaches.set(session, cache) }
  return cache
}

const itemLabels = (detail: Record<string, unknown>): readonly string[] => {
  const items = detail.items
  if (!Array.isArray(items)) return []
  return items
    .map((raw) => {
      const item = (raw ?? {}) as Record<string, unknown>
      const name = displayTitle(item.item_name ?? item.dish_name ?? item.name)
      if (!name) return ''
      const qty = Number(item.quantity ?? item.qty ?? 1)
      return qty > 1 ? `${name} ×${qty}` : name
    })
    .filter(Boolean)
}

/** Bounded detail reads restart when the authenticated runtime becomes live. */
export const useOrderItemNamesState = (orderIds: readonly string[]) => {
  const session = useSyncExternalStore(subscribePrototypeRuntime, getActivePrototypeRuntime, getActivePrototypeRuntime)
  const live = isLiveRuntime()
  const wanted = [...new Set(orderIds.filter((id) => id && (!live || /^[0-9a-f]{8}(-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i.test(id))))].slice(0, ITEM_FETCH_LIMIT)
  const [attempt, setAttempt] = useState(0)
  const key = JSON.stringify([wanted, live, attempt])
  const [loaded, setLoaded] = useState<{ key: string; session: object; byId: Record<string, readonly string[]>; failedIds: string[] }>({ key: '', session, byId: {}, failedIds: [] })
  useEffect(() => {
    if (!live || wanted.length === 0) return
    let alive = true
    const cache = namesCache(session)
    const failedIds: string[] = []
    const byId: Record<string, readonly string[]> = {}
    for (const id of wanted) {
      const hit = cache.get(id)
      if (hit) byId[id] = hit
    }
    const pending = wanted.filter((id) => !cache.has(id))
    const publish = () => {
      if (alive) setLoaded({ key, session, byId: { ...byId }, failedIds: [...failedIds] })
    }
    publish()
    const fetchOne = async (id: string) => {
      for (let n = 0; n < 3 && alive; n++) {
        try {
          const detail = commandPayload(await commands.marketplace.getOrder(id))
          if (!Array.isArray(detail.items)) throw new Error('Order details are incomplete.')
          const labels = itemLabels(detail)
          if (labels.length === 0) throw new Error('Meal items are unavailable.')
          byId[id] = labels
          cache.set(id, labels)
          if (cache.size > 256) cache.delete(cache.keys().next().value!)
          return
        } catch {
          if (n < 2 && alive) await new Promise((resolve) => window.setTimeout(resolve, 1000 * (n + 1)))
        }
      }
      if (alive) failedIds.push(id)
    }
    void (async () => {
      for (let offset = 0; offset < pending.length && alive; offset += 4) {
        await Promise.all(pending.slice(offset, offset + 4).map(fetchOne))
        publish()
      }
    })()
    return () => { alive = false }
    // The key represents the bounded order list and retry generation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, live, session])
  const current = loaded.key === key && loaded.session === session
  return {
    byId: current ? loaded.byId : {}, failedIds: current ? loaded.failedIds : [],
    pendingIds: live ? wanted.filter(id => !current || (!loaded.byId[id] && !loaded.failedIds.includes(id))) : [],
    retry: () => setAttempt(value => value + 1),
  }
}

export const useOrderItemNames = (orderIds: readonly string[]): Record<string, readonly string[]> =>
  useOrderItemNamesState(orderIds).byId
